import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { CommonModule, AsyncPipe, DatePipe } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { PrivateMessage, PrivateMessageQueryType, PrivateMessageTypeHumanized } from '../../../models/privateMessage'
import { AlertController, IonicModule, IonInfiniteScroll, ModalController } from '@ionic/angular'
import { PrivateMessagesService } from '../../../services/domain/privateMessages/private-messages.service'
import { PrivateMessageViewerComponent } from '../private-message-viewer/private-message-viewer.component'
import { AssistanceEditorModalComponent } from '../../assistance/assistance-editor-modal/assistance-editor-modal.component'
import { RecurrenceEditingMode } from '../../recurrence/recurrenceEditing'
import {
  faAngleDoubleRight,
  faEnvelope,
  faEnvelopeOpen,
  faHandsHelping,
  faPaperclip,
} from '@fortawesome/free-solid-svg-icons'
import { NgxFloatUiContentComponent, NgxFloatUiModule, NgxFloatUiTriggers } from 'ngx-float-ui'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { AssistantSelectComponent } from '../../assistant/assistant-select/components/assistant-select/assistant-select.component'
import { AssistantDropdownComponent } from '../../assistant/assistant-dropdown/assistant-dropdown.component'
import { ClientDropdownComponent } from '../../client/client-dropdown/client-dropdown.component'
import { PrivateMessageTypeToggleComponent } from './private-message-type-toggle/private-message-type-toggle.component'
import { MessagingPageService } from '../../../pages/messaging/messaging/messaging-page.service'
import { UsersService } from '../../../services/domain/users.service'
import { User } from '../../../models/user'
import { CoordinatorDropdownComponent } from '../../coordinator/coordinator-dropdown/coordinator-dropdown.component'
import { MessageDirection } from '../private-message-list-wrapper/private-message-list-wrapper.component'
import { StripTagsPipe } from '../../../shared/pipes/stripHtml.pipe'
import { FeatureFlags } from '../../../shared/featureFlags'

@Component({
  selector: 'app-private-messages-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    NgxFloatUiModule,
    FontAwesomeModule,
    AssistantDropdownComponent,
    ClientDropdownComponent,
    PrivateMessageTypeToggleComponent,
    CoordinatorDropdownComponent,
    AsyncPipe,
    DatePipe,
    StripTagsPipe,
  ],
  templateUrl: './private-messages-list.component.html',
  styleUrls: ['./private-messages-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrivateMessagesListComponent implements OnInit, OnChanges {
  private alertController = inject(AlertController)
  private modalCtrl = inject(ModalController)
  private privMsgService = inject(PrivateMessagesService)
  service = inject(MessagingPageService)
  private usersService = inject(UsersService)
  private cdr = inject(ChangeDetectorRef)

  TAG = 'PrivateMessagesListComponent'

  @Input() messageDirection: MessageDirection
  @Input() sortedMessages: PrivateMessage[]
  @Input() allowFilters = true
  @Input() allowReadUnreadControl = true

  @Output() loadMessagesFromOffset = new EventEmitter<number>()
  @Output() replyToMessage = new EventEmitter<PrivateMessage>()
  @Output() filterByAssistantId = new EventEmitter<number>()
  @Output() filterByClientId = new EventEmitter<number>()
  @Output() filterByUserId = new EventEmitter<number>()
  @Output() fetchOnlyUnread = new EventEmitter<boolean>()

  @ViewChild('infiniteScroll') infiniteScroll: IonInfiniteScroll
  @ViewChild('popperMarkAllSelectedAsUnread') popperMarkAllSelectedAsUnread: NgxFloatUiContentComponent
  @ViewChild('popperMarkAllSelectedAsRead') popperMarkAllSelectedAsRead: NgxFloatUiContentComponent
  @ViewChild('popperMarkAllAsRead') popperMarkAllAsRead: NgxFloatUiContentComponent

  private assistanceOpen = false
  faPaperclip = faPaperclip
  faHandsHelping = faHandsHelping
  faAngleDoubleRight = faAngleDoubleRight
  faEnvelope = faEnvelope
  faEnvelopeOpen = faEnvelopeOpen

  selectedMessageIds = new Set<number>()
  toolbarVisible = false

  PrivateMessageType = PrivateMessageTypeHumanized
  PrivateMessageQueryType = PrivateMessageQueryType
  selectAllChecked = false
  currentUser: User
  messageType$ = this.service.inboundMsgType$

  async ngOnInit(): Promise<void> {
    this.currentUser = await this.usersService.getCurrentUserPromise()
  }

  ngOnChanges(changes: SimpleChanges): void {
    for (const propName in changes) {
      if (changes.hasOwnProperty(propName)) {
        if (propName === 'sortedMessages') {
          this.infiniteScroll?.complete()
        }
      }
    }
  }

  trackByMsg(index: number, item: PrivateMessage) {
    return item.id
  }

  async openMessage(msg: PrivateMessage) {
    console.log(`${this.TAG}#openMessage`, msg)
    const modal = await this.modalCtrl.create({
      component: PrivateMessageViewerComponent,
      componentProps: { message: msg },
      cssClass: ['privateMessageViewerModal', 'auto-height'],
    })

    modal.onDidDismiss().then((data) => {
      const command = data.data
      if (command === 'reply') this.replyToMessage.emit(msg)
    })

    await modal.present()

    this.privMsgService.markMessageRead(msg)
  }

  async openAssistance(assistanceId: number) {
    if (this.assistanceOpen) {
      console.log(this.TAG, 'Editor already open')
      return
    }

    let modal
    if (this.currentUser.isOaCoordinator()) {
      modal = await this.modalCtrl.create({
        component: AssistanceEditorModalComponent,
        componentProps: {
          urid: assistanceId.toString(),
          recurrenceEditingMode: RecurrenceEditingMode.none,
        },
        backdropDismiss: false,
        id: AssistanceEditorModalComponent.name,
      })
    } else if (this.currentUser.isOaAssistant()) {
      modal = await this.service.createRoleAssistantAssistanceEditorModal(assistanceId, this.currentUser)
    }

    this.assistanceOpen = true
    modal.onDidDismiss().then(() => {
      this.assistanceOpen = false
    })
    return await modal.present()
  }

  onMessageSelectionChange(msg: PrivateMessage, checked: boolean) {
    if (checked) {
      this.selectedMessageIds.add(msg.id)
    } else {
      this.selectedMessageIds.delete(msg.id)
    }
  }

  isMessageChecked(id: number) {
    return this.selectedMessageIds.has(id)
  }

  async markSelectedAsUnread() {
    await this.privMsgService.markMessagesUnread(Array.from(this.selectedMessageIds))
    this.selectedMessageIds.clear()
    this.selectAllChecked = false
  }
  async markSelectedAsRead() {
    await this.privMsgService.markMessagesRead(Array.from(this.selectedMessageIds))
    this.selectedMessageIds.clear()
    this.selectAllChecked = false
  }
  async markAllAsReadWithConfirm() {
    const alert = await this.alertController.create({
      header: 'Opravdu?',
      message: 'Označit všechny zprávy jako přečtené?',
      buttons: [
        {
          text: 'Ne',
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {},
        },
        {
          text: 'Označit jako přečtené',
          cssClass: 'primary',
          handler: () => {
            this.privMsgService.markAllMessagesRead()
          },
        },
      ],
    })

    await alert.present()
  }

  shouldBeSelectAllCheckboxIndeterminate(sortedMessages: PrivateMessage[], selectedMsgIds: Set<number>) {
    if (!selectedMsgIds.size) return false
    return sortedMessages.length !== selectedMsgIds.size
  }

  toggleSelectAll(checked: boolean) {
    if (checked) {
      this.selectedMessageIds = new Set([...this.sortedMessages.map((m) => m.id)])
    } else {
      this.selectedMessageIds.clear()
    }
  }

  loadMoreMessages() {
    this.loadMessagesFromOffset.emit(this.sortedMessages.length)
  }

  areAllMessagesSelected() {
    return this.sortedMessages?.length === this.selectedMessageIds?.size
  }

  toggleToolbarVisibility() {
    this.toolbarVisible = !this.toolbarVisible
    this.cdr.markForCheck()
  }

  protected readonly NgxFloatUiTriggers = NgxFloatUiTriggers
  protected readonly FeatureFlags = FeatureFlags
}
